import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { withClientClock } from '@common/services/client-clock-fields';
import { setCalendarSidebarFilters, setCurrentDate, setSelectedEventDate, setView } from '@common-reducers/CalendarReducer';
import {
    editCalendarEventThunk,
    handleClickWeedTimeSlotThunk,
    handleNewMessageDialogThunk,
    handleRecurringEventDragThunk,
    onDeleteRecurringDialogCloseThunk,
    onRecurringDialogCloseThunk,
} from '@common-reducers/CalendarThunks';
import { DBUserMessageThunks, campaignsSelectOneObjectByQuery, dripsSelectOneObjectByQuery } from '@common-reducers/DBServiceThunks';
import { getPendingAndRuningCampaigns, onCampaignEventDropThunk, onDripStepDropThunk } from '@common-reducers/CampaignThunks';
import { AUDIENCE_LIST_QUERY, DBBoardsThunks } from '@common-reducers/DBServiceThunks';
import { deleteUserMessage, getPendingUserMessages } from '@common/thunks/UserMessagesThunks';
import { loadSentHistoryForRange } from '@common-reducers/planner-sent-history';
import { applyMomentLocale, formatInLanguage } from '@common/config/moment-locales';
import { namespace } from '@common-config/constants';
import { getCalendarVisibleRange, CalendarViewName } from '../calendar-range';
import { getUserStartDayOfWeek } from '@common-reducers/UserSelectors';
import { getCalendarLeanEvents } from '@common-reducers/WhatsAppSelectors';
import { isNarrowed, normalizePlannerFilters } from '@common/services/planner-filters';
import { MessageConfirmationDialog } from '@extension/context/content/components/add-scheduled-message-button/message-confirmation-dialog';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { toastDBCreateError } from '@common/utils/db-create-error-toast';
import { useNavigate } from 'react-router-dom';
import { isExtensionContext } from '@common/services/detect-context';
import { campaignPagePath, navigateToCampaignPage } from '@common-views/campaigns-redesign/open-campaign-page';
import { isRtlLanguage } from '@common/services/rtl';
import { confirmationDialogOptions } from '../recurring-dialog-options';
import { planOccurrenceDelete, resolveDeleteConfirmation, resolveStepSkipConfirmation, resolveStepUnskipConfirmation, isChipStepSkipped } from './layout/delete-occurrence';
import type { PendingStepSkipEntry } from '@common-reducers/recurring-occurrence-filter';
import { useSkipDripOccurrenceMutation, useSkipDripStepMutation, useUnskipDripStepMutation } from '@common-reducers/backend-api/backend-api';
import { Menu, MenuItem } from '@mui/material';
import { destinationName } from './layout/destination-name';
import { sendKindOf } from '@common/components/kind-glyphs/send-kind';
import { resolveCalendarEditTarget } from '../calendar-edit-target';
import styles from './calendar-grid.module.scss';
import { CalendarToolbarV2 } from './calendar-toolbar-v2';
import { emptyPeriodCopy, emptyPeriodText } from './empty-state-copy';
import { useSentHistoryStatus } from './use-sent-history-status';
import { useDelayedBusy } from './use-delayed-busy';
import { ChipHoverCard } from './chip-hover-card';
import type { HoverTarget } from './chip-hover-card';
import { DayPanel } from './day-panel';
import { DragPreview } from './drag-preview';
import { buildMonthLayout } from './layout/month-layout';
import { columnIndexAt, snapToSlot } from './layout/drag-model';
import type { DropResolution } from './layout/drag-model';
import { MonthGrid } from './month-grid';
import { PX_PER_HOUR, SLOT_MINUTES, TimeGrid } from './time-grid';
import { useCalendarDrag } from './use-calendar-drag';
import { isProjectedDripStep } from './layout/occurrence-key';
import { pickRevealKey, shouldActOnReveal } from './layout/reveal-occurrence';
import { DROP_PREVIEW_REFUSAL_KEY, previewDripStepDrop } from './layout/drop-preview';
import type { CalendarGridView, CalendarLeanEvent } from './types';

/**
 * #592c / BE#648 — the single-STEP skip (and its un-skip) is HIDDEN on the calendar until BE#648.
 * A calendar chip can target a FUTURE occurrence, whose instant the FE derives from its own
 * DST-adjusted rrule expansion; that diverges from the BE's UTC roll dueDate across a DST boundary,
 * so a pending keyed to the FE instant would never be consumed (silent skip loss — see the backend
 * m1-fe-be-dst-agreement test). The current-cycle single-step skip still ships via the sequence
 * detail (step cards), which uses the drip's own stored dueDate verbatim and is DST-immune. The
 * per-date "Skip whole cycle" (#592b) is a separate shipped feature and stays. Flip to `true` once
 * #648 makes the FE↔BE occurrence instants agree (TZID-aware).
 */
const CALENDAR_STEP_SKIP_ENABLED = false;

/** Why a sequence refused a drop, said in the user's language. */
const DRIP_DROP_ERROR_KEY: Record<string, string> = {
    'before-previous-step': 'calendar.grid.dripErrorBeforePrevious',
    'step-started': 'calendar.grid.dripErrorStarted',
    'not-found': 'calendar.grid.dropErrorGeneric',
    'unknown-step': 'calendar.grid.dropErrorGeneric',
};

const MAX_CHIPS_PER_CELL = 3;

/**
 * Pulse an element so the eye lands on it — a chip (`.chipRevealing`: a ring
 * in its OWN identity colour, the rail the sidebar row carries too) or, in the
 * month view, its day cell (`.cellRevealing`). The colour and the motion live
 * in CSS (theme tokens, reduced-motion); JS only starts it, and restarts it on
 * a repeat click.
 */
function flash(el: HTMLElement, className: string) {
    el.classList.remove(className);
    void el.offsetWidth; // restart the animation when the class is re-added
    el.classList.add(className);
    // Removed by the clock too, not only by `animationend`: a CSS animation only
    // advances while frames are painted, so on a throttled/background tab the end
    // event never fires and the highlight would stay on for good.
    const done = () => el.classList.remove(className);
    el.addEventListener('animationend', done, { once: true });
    setTimeout(done, 2600);
}

/**
 * The extension renders this calendar in a shadow-DOM overlay with NO react-router
 * Router (campaign routing there is the campaignsMemoryRouter singleton), so
 * calling useNavigate() unconditionally would crash the whole calendar there.
 * This bridge is rendered only on the website and publishes the browser router's
 * navigate into a ref the click handler reads.
 */
function WebsiteNavigateBridge({ navigateRef }: { navigateRef: React.MutableRefObject<((path: string) => void) | null> }) {
    const navigate = useNavigate();
    navigateRef.current = navigate;
    return null;
}

const CAMPAIGN_DROP_ERROR_KEY: Record<string, string> = {
    not_found: 'calendar.grid.dropErrorSequence',
    not_pending: 'calendar.grid.dropErrorStarted',
    too_close: 'calendar.grid.dropErrorTooClose',
};

/**
 * The in-house planner calendar.
 *
 * Rendering and interaction only: every scheduling decision still belongs to
 * the selectors and thunks this component calls. One drag engine serves the
 * month grid, the time grid and the day panel, so a row can be dragged from the
 * panel onto the grid.
 */
export const CalendarGridV2: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation(namespace);
    // #592b — per-date recurring-drip skip (calendar chip).
    const [skipDripOccurrence] = useSkipDripOccurrenceMutation();
    // #592c — single-STEP recurring-drip skip (calendar chip menu).
    const [skipDripStep] = useSkipDripStepMutation();
    // #592c — undo a single-STEP skip from the chip menu (non-destructive, no confirm).
    const [unskipDripStep] = useUnskipDripStepMutation();

    const events = useAppSelector(getCalendarLeanEvents) as CalendarLeanEvent[];
    // An empty grid means two very different things — "you have nothing scheduled" and
    // "your filters are hiding it" — and saying the first when the second is true is what
    // made the filters look broken. The toolbar needs to know which it is.
    const plannerFilters = useAppSelector(state => state.CalendarReducer.calendarSidebarFilters);
    const plannerIsNarrowed = isNarrowed(plannerFilters);
    const rawView = useAppSelector(state => state.CalendarReducer.view);
    const currentDate = useAppSelector(state => state.CalendarReducer.currentDate);
    const weekStartsOn = (useAppSelector(getUserStartDayOfWeek) ?? 0) as 0 | 1 | 2 | 3 | 4 | 5 | 6;

    const view: CalendarGridView = rawView === 'week' || rawView === 'day' ? rawView : 'month';
    const date = useMemo(() => new Date(currentDate as any), [currentDate]);
    const isRtl = isRtlLanguage(i18n.language);

    // The "now" reference is shared by every layout + status decision. A minute
    // tick keeps the now-line and the five-minute campaign lock honest without
    // re-rendering on every frame.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);

    // Data loading lives with the calendar, not with one implementation of it:
    // the web /calendar route renders this component directly, so a refresh has
    // to load its own messages, campaigns (+ their drip docs) and audience boards.
    //
    // Tracked, so the toolbar's loading indicator covers the calendar's first load too — it
    // follows these real requests and goes off when the last one settles, whatever the
    // outcome (allSettled: an error can never leave it spinning).
    const [bootLoading, setBootLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        Promise.allSettled([
            dispatch(getPendingUserMessages()),
            dispatch(getPendingAndRuningCampaigns()),
            dispatch(DBBoardsThunks.find({ type: 'audience', deleted: { $ne: true }, ...AUDIENCE_LIST_QUERY })),
        ]).finally(() => { if (alive) setBootLoading(false); });
        return () => { alive = false; };
    }, [dispatch]);

    // Month/day names follow the UI language.
    useEffect(() => {
        applyMomentLocale(i18n.resolvedLanguage || i18n.language || 'en');
    }, [i18n.language, i18n.resolvedLanguage]);

    // moment's week start drives both the month grid and the week columns.
    useEffect(() => {
        moment.updateLocale('en', { week: { dow: weekStartsOn } });
    }, [weekStartsOn]);


    const [panelDate, setPanelDate] = useState<Date | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    /**
     * The ONE occurrence a delete has been asked about — nothing more. It is a
     * pending QUESTION, not a pending write: the request is built and dispatched
     * only from the dialog's confirm branch, so a cancel, an Escape or a
     * navigation leaves the series exactly as it was.
     */
    const [deleteTarget, setDeleteTarget] = useState<CalendarLeanEvent | null>(null);
    // #592c — the recurring-drip chip menu ("Skip this message" / "Skip whole
    // cycle"), anchored at the pointer, and the step-skip confirm it can open.
    const [skipMenu, setSkipMenu] = useState<{ event: CalendarLeanEvent; x: number; y: number } | null>(null);
    const [skipStepTarget, setSkipStepTarget] = useState<CalendarLeanEvent | null>(null);
    const dragActiveRef = useRef(false);
    const [recurringDrag, setRecurringDrag] = useState<any | null>(null);
    const [pastDrop, setPastDrop] = useState<{ event: CalendarLeanEvent; dueDate: string } | null>(null);

    // One hover card for the whole grid, opened after a short dwell so that
    // sweeping the pointer across a dense month does not flash cards.
    const [hover, setHover] = useState<HoverTarget | null>(null);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearHover = useCallback(() => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
        setHover(null);
    }, []);
    const onHoverStart = useCallback((event: CalendarLeanEvent, rect: DOMRect) => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => setHover({
            event,
            rect: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom },
        }), 320);
    }, []);
    useEffect(() => () => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); }, []);

    const websiteNavigateRef = useRef<((path: string) => void) | null>(null);

    const days = useMemo(() => {
        if (view === 'day') return [moment(date).startOf('day').toDate()];
        const start = moment(date).startOf('week');
        return Array.from({ length: 7 }, (_, i) => start.clone().add(i, 'days').toDate());
    }, [view, date]);

    const monthLayout = useMemo(
        () => buildMonthLayout(events, {
            monthStart: moment(date).startOf('month').toDate(),
            weekStartsOn,
            maxChipsPerCell: MAX_CHIPS_PER_CELL,
            now,
        }),
        [events, date, weekStartsOn, now],
    );

    // Geometry, not DOM hit-testing: which day/slot sits under the pointer
    // follows from the grid's own rect. This is what lets the same code drag
    // inside the extension's shadow root with no patching.
    const resolveTargetAt = useCallback((clientX: number, clientY: number, rect: DOMRect) => {
        // Columns FLOW with the document direction, so in Hebrew the first day is
        // on the right; ask the grid element itself rather than trusting a prop,
        // since dir can be set on <html>, on <body>, or on a wrapper.
        const body = gridRef.current;
        const rtlGrid = body ? getComputedStyle(body).direction === 'rtl' : isRtl;

        if (view === 'month') {
            const column = columnIndexAt(clientX, rect, 7, rtlGrid);
            if (column === null) return null;
            // The month body SCROLLS, so the visible rect is shorter than the grid:
            // dividing the rect by the row count lands the drop a week or more away
            // from where the user released. Measure the real row pitch and add the
            // scroll offset.
            const scrollTop = body?.scrollTop ?? 0;
            const contentHeight = body?.scrollHeight || rect.height;
            const rowHeight = contentHeight / monthLayout.weeks.length;
            const row = Math.floor((clientY - rect.top + scrollTop) / rowHeight);
            if (row < 0 || row >= monthLayout.weeks.length) return null;
            return monthLayout.weeks[row][column].date.getTime();
        }
        const dayIndex = columnIndexAt(clientX, rect, days.length, rtlGrid);
        if (dayIndex === null) return null;
        const hours = (clientY - rect.top) / PX_PER_HOUR;
        if (hours < 0 || hours >= 24) return null;
        const at = moment(days[dayIndex]).startOf('day').add(hours * 60, 'minutes').valueOf();
        return snapToSlot(at, SLOT_MINUTES);
    }, [view, monthLayout, days, isRtl]);

    /**
     * The refusal the drag preview actually SHOWED, read back by `onDrop`.
     *
     * A dialog repeating a refusal the user has just read, in place, about a move
     * they have already abandoned, is noise. But suppressing by REASON would have
     * been wrong: the preview stays silent when the drip is not in the store, and
     * a drop refused for a reason nobody was shown must still say something. So
     * the drop compares against what was on screen, not against a list of reasons
     * that are usually visible.
     */
    const shownRefusalRef = useRef<string | null>(null);

    // #1257: the generic patch now rejects when nothing was saved. A dropped chip must then
    // go back to where it was (rollback) and the user must be told, instead of the chip
    // showing a move that never reached the server.
    const moveDueDate = useCallback(async (id: string, dueDate: Date | string) => {
        const res = await dispatch(DBUserMessageThunks.patch({
            entity: withClientClock({ _id: id, dueDate }) as any, // FE#447: the drop target was picked on THIS machine's clock
            options: { inCaseOfErrorRollbackOptimisticUpdateDisabled: true },
        }));
        if (DBUserMessageThunks.patch.rejected.match(res)) {
            toastDBCreateError(res.payload ?? res.error, t('calendar.grid.changeNotSaved', 'Your change was not saved.'));
        }
    }, [dispatch, t]);

    const onDrop = useCallback(async (event: CalendarLeanEvent, resolution: DropResolution) => {
        if (resolution.kind === 'noop') return;

        if (resolution.kind === 'recurring-scope-prompt') {
            // Nothing moves yet: RRuleRecurrenceHandler decides what happens once
            // the user picks a scope, and the store change is what redraws the grid.
            const full = await dispatch(handleRecurringEventDragThunk({
                leanEvent: event as any,
                newDueDate: resolution.dueDate,
            })).unwrap().catch((e: unknown) => {
                // Same rule as the drip branch below: a drop that throws must
                // say so. Swallowed, the chip snapped back and nothing else
                // happened — indistinguishable from a drop that was ignored.
                console.error('[calendar] recurring drop failed', e);
                setWarning(t('calendar.grid.dropErrorGeneric'));
                return null;
            });
            if (full) setRecurringDrag(full);
            return;
        }

        // A drip step is not an independent date: dragging it re-times the
        // SEQUENCE (move the start, or change the gap that places this step),
        // which is the only way the drip's projection and the queued campaign
        // can agree afterwards.
        if (event.isCampaign && event.dripId && typeof event.dripStepIndex === 'number') {
            // Read BEFORE the await: the gesture is already being torn down, and by
            // the time the thunk resolves the ref has been cleared for the next drag.
            const shownRefusal = shownRefusalRef.current;
            const result: any = await dispatch(onDripStepDropThunk({
                dripId: event.dripId,
                stepIndex: event.dripStepIndex,
                targetMs: new Date(resolution.dueDate).getTime(),
                // Only a MATERIALIZED step has a campaign document; the later
                // ones are projections and carry the synthetic `dripId:step:N`.
                campaignId: isProjectedDripStep(event._id) ? undefined : event._id,
            })).unwrap().catch((e: unknown) => {
                // A thrown drop used to vanish here: no move, no message.
                console.error('[calendar] drip step drop failed', e);
                return { success: false, refusal: 'threw' };
            });
            // A refusal the drag already showed in place needs no dialog: the user
            // was told before they let go, and repeating it afterwards is a modal
            // about a decision they have already seen made. Anything the preview
            // could NOT foresee (the drip vanished, the write threw) still has to
            // be said out loud, or the drop is silent.
            if (result && result.success === false && result.refusal !== shownRefusal) {
                setWarning(t(DRIP_DROP_ERROR_KEY[result.refusal] ?? 'calendar.grid.dropErrorGeneric'));
            }
            return;
        }

        if (event.isCampaign) {
            const result: any = await dispatch(onCampaignEventDropThunk({
                campaignId: event._id,
                start: resolution.dueDate,
                view,
            })).unwrap().catch((e: unknown) => {
                console.error('[calendar] campaign drop failed', e);
                return { success: false, errorType: 'threw' };
            });
            if (result && result.success === false && result.errorType && result.errorType !== 'no_change') {
                setWarning(t(CAMPAIGN_DROP_ERROR_KEY[result.errorType] ?? 'calendar.grid.dropErrorGeneric'));
            }
            return;
        }

        if (resolution.kind === 'past-warning') {
            setPastDrop({ event, dueDate: resolution.dueDate });
            return;
        }

        // A drag changes exactly one field, so it writes exactly one field.
        // scheduleMessageFromLeanEventThunk decides it "already has the full
        // message" from `'message' in leanEvent` — always true for a calendar
        // event — so it never loads the real document and PATCHes the whole
        // entity with the lean projection instead: a poll loses its answer
        // options, a multi-recipient message loses every recipient but the
        // first, and a sent or failed occurrence is re-armed as pending. One
        // nudge on the calendar could therefore destroy the message it moved.
        void moveDueDate(event._id, resolution.dueDate);
    }, [dispatch, view, t, moveDueDate]);


    const { gridRef, gridHandlers, drag, beginDrag, consumeDragClick } = useCalendarDrag({ view, resolveTargetAt, onDrop });

    // Whether the cell under the pointer would take this drop, decided WHILE the
    // drag is in the air rather than after the user commits to it. Every cell
    // used to light up identically, including the ones the sequence forbids, and
    // the refusal arrived — if at all — as a dialog about a move the user had
    // already made in their head.
    const dripSlice = useAppSelector(state => (state as any).DBDripReducer);
    const campaignSlice = useAppSelector(state => (state as any).DBCampaignReducer);
    const dropRefusal = useMemo(() => {
        const event = drag?.active ? drag.event : null;
        if (!event?.isCampaign || !event.dripId || typeof event.dripStepIndex !== 'number') return null;
        const drip: any = dripsSelectOneObjectByQuery(dripSlice, { _id: event.dripId });
        if (!drip) return null;   // not loaded — say nothing rather than guess
        const stepZero: any =
            campaignsSelectOneObjectByQuery(campaignSlice, { dripId: event.dripId, dripStepIndex: 0 })
            ?? campaignsSelectOneObjectByQuery(campaignSlice, { dripId: event.dripId });
        const startIso = stepZero?.schedule?.dueDate ?? drip.schedule?.dueDate;
        return previewDripStepDrop({
            startMs: startIso ? new Date(startIso).getTime() : null,
            steps: drip.steps,
            stepIndex: event.dripStepIndex,
            targetMs: drag?.targetMs ?? null,
            stepHasStarted: !!event.campaignStatus && event.campaignStatus !== 'pending',
            isRecurringDrip: !!drip.schedule?.isRecurring,
        });
    }, [drag?.active, drag?.event, drag?.targetMs, dripSlice, campaignSlice]);

    const dropRefusalText = dropRefusal ? t(DROP_PREVIEW_REFUSAL_KEY[dropRefusal]) : null;
    // Read at drop time, and only ever set while a drag is live, so a stale value
    // from a previous gesture cannot swallow the next drop's message.
    shownRefusalRef.current = drag?.active ? dropRefusal : null;
    // Read by `onRequestDelete`, which must stay identity-stable (every chip is
    // memoised on it) and therefore cannot close over `drag`.
    dragActiveRef.current = !!drag?.active;

    const onOpenEvent = useCallback((event: CalendarLeanEvent, mouse?: { x: number; y: number }) => {
        // A drop ends in a click on the chip that was dragged. That click is part
        // of the move, not a request to open anything.
        if (consumeDragClick()) return;

        // Selecting the occurrence first: a recurring series expands into many
        // events that share one document id, so every downstream action needs
        // THIS occurrence's date, not the document's.
        dispatch(setSelectedEventDate(moment(event.dueDate).toISOString() as any));

        // A click OPENS the thing — there is no in-between Edit/Delete menu. A
        // campaign (any step of a sequence, or a standalone one) opens its page,
        // where it is viewed and edited; a message or Status opens its editor,
        // exactly as the menu's Edit did. Delete stays reachable from the editor,
        // and from the chip's right-click / Delete key (`onRequestDelete`).
        if (event.isCampaign) {
            const target = resolveCalendarEditTarget(event as any);
            if (target) {
                // The chip that was clicked IS one specific step, and arriving at a
                // sequence with no idea which one is the bug — so the index rides
                // along (see campaignPagePath).
                navigateToCampaignPage(campaignPagePath(target.campaignId, event.dripStepIndex, 'calendar'), {
                    dispatch,
                    navigateWeb: (path) => websiteNavigateRef.current?.(path),
                });
            }
            return;
        }

        // A send that already went out is a record, not a plan: the editor refuses
        // it on purpose (editCalendarEventThunk — saving it could queue a second
        // send). Showing it is the answer, so the click opens the chip's details
        // card at once, where the hover would have opened it after a beat — the
        // message, who it went to, and that it was sent. It closes like a hover.
        if (event.isSentRecord === true) {
            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
            const at = mouse ?? { x: 0, y: 0 };
            setHover({ event, rect: { top: at.y, left: at.x, right: at.x, bottom: at.y } });
            return;
        }

        dispatch(editCalendarEventThunk(event as any));
    }, [dispatch, consumeDragClick]);

    /**
     * Asked to remove the occurrence a chip stands for.
     *
     * Deliberately does NOT consume the drag-tail click token: a right-click and
     * the Delete key are not how a drag ends, so swallowing the token here would
     * make the NEXT ordinary click on a chip do nothing.
     */
    const onRequestDelete = useCallback((event: CalendarLeanEvent, mouse?: { x: number; y: number }) => {
        // A gesture made mid-drag is not an answer to anything: the chip is in
        // the air and the question would be about where it started.
        if (dragActiveRef.current) return;
        clearHover();
        // #592c — a recurring-drip occurrence offers TWO skips (this message /
        // whole cycle), so it opens a menu at the pointer rather than going
        // straight to a dialog. Everything else keeps the single delete/skip
        // dialog. `planOccurrenceDelete` returns `skip-drip-date` for exactly the
        // synthetic drip-occurrence chips (keyed on `dripOccurrenceStartMs`).
        if (planOccurrenceDelete(event).kind === 'skip-drip-date') {
            setSkipMenu({ event, x: mouse?.x ?? 0, y: mouse?.y ?? 0 });
            return;
        }
        setDeleteTarget(event);
    }, [clearHover]);

    const onCreateAt = useCallback((at: Date) => {
        if (view === 'month') {
            // BE#657 (a) — a month "+" on a FUTURE day is a real date pick (seed it). But on TODAY's
            // cell the thunk would seed today@NOW (the operator's near-now / "already passed" symptom),
            // which the user didn't choose — open a fresh compose there instead (pick-a-time, gated).
            const isToday = new Date(at).toDateString() === new Date().toDateString();
            dispatch(handleNewMessageDialogThunk(
                isToday ? { fresh: true } : { slotInfo: { start: at, end: at, slots: [at], action: 'click' as const } },
            ));
            return;
        }
        dispatch(handleClickWeedTimeSlotThunk({
            event: { stopPropagation: () => undefined } as any,
            date: at,
        }));
    }, [dispatch, view]);

    // What the toolbar counts must be what the user can see. Counting every event
    // in the store told a user looking at September that they had "9 scheduled
    // sends" when some of those were in July and some in October.
    const visibleCount = useMemo(() => {
        if (view === 'month') {
            return monthLayout.weeks.flat().reduce((sum, cell) => sum + (cell.inMonth ? cell.total : 0), 0);
        }
        const dayKeys = new Set(days.map(d => moment(d).format('YYYY-MM-DD')));
        return events.filter(e => dayKeys.has(moment(e.dueDate).format('YYYY-MM-DD'))).length;
    }, [view, monthLayout, days, events]);

    // What already went out is fetched ONLY while "Show sent" is on, only for the range on
    // screen, only for the kinds that are on, and never twice for a range already loaded —
    // `loadSentHistoryForRange` decides all of that, so this just says "this is the range".
    const showSentOn = normalizePlannerFilters(plannerFilters).showSent;
    useEffect(() => {
        if (!showSentOn) return;
        const { start, end } = getCalendarVisibleRange(currentDate as any, view as CalendarViewName);
        dispatch(loadSentHistoryForRange({ start, end }));
    }, [dispatch, showSentOn, plannerFilters, currentDate, view]);

    const emptyCopy = emptyPeriodCopy(view, { narrowed: plannerIsNarrowed, showSent: showSentOn });
    // The empty-month caption can be closed. Once closed it stays closed while the
    // planner is open — the toolbar still carries the same sentence.
    const [emptyNoteDismissed, setEmptyNoteDismissed] = useState(false);
    // Every request for the range on screen — the first load and "Show sent"'s range fetch —
    // feeds one indicator in the toolbar (and the Show sent switch reads the same state).
    const sentHistory = useSentHistoryStatus();
    const rangeLoading = bootLoading || sentHistory.loading;
    const busyVisible = useDelayedBusy(rangeLoading);
    const showSent = useCallback(
        () => dispatch(setCalendarSidebarFilters({ ...normalizePlannerFilters(plannerFilters), showSent: true })),
        [dispatch, plannerFilters],
    );

    const panelEvents = useMemo(
        () => (panelDate ? events.filter(e => moment(e.dueDate).isSame(panelDate, 'day')) : []),
        [events, panelDate],
    );

    // A card hanging over the grid while dragging would obscure the drop target.
    useEffect(() => { if (drag?.active) clearHover(); }, [drag?.active, clearHover]);

    // "Show on calendar" from a sidebar row: move the grid to the occurrence,
    // scroll its chip into view and flash it, so there is no doubt which chip
    // the row is. The chip is found in the DOM (every chip carries its
    // occurrence key) — that is the only place that knows whether it is on
    // screen, in a month cell's "+N more", or in a time column below the fold.
    const revealRequest = useAppSelector(state => state.CalendarReducer.revealRequest);
    const rootRef = useRef<HTMLDivElement | null>(null);
    // The request stays in the store after it is handled, so a later mount of
    // this grid (opening the planner again) must not replay it — see
    // shouldActOnReveal.
    const handledRevealRef = useRef<number | null>(null);
    useEffect(() => {
        if (!revealRequest || !shouldActOnReveal(revealRequest, handledRevealRef.current, Date.now())) return;
        handledRevealRef.current = revealRequest.nonce;
        const target = new Date(revealRequest.dueDate);
        if (Number.isNaN(target.getTime())) return;
        setPanelDate(null);
        clearHover();
        dispatch(setCurrentDate(target));

        let attempts = 0;
        let dayFlashed = false;
        let timer: ReturnType<typeof setTimeout>;
        const attempt = () => {
            // getRootNode: in the extension the grid lives in a shadow root.
            const scope = (rootRef.current?.getRootNode() ?? document) as Document | ShadowRoot;
            // In the month view the DAY flashes too — the answer to "when" at a
            // glance, and the only visible answer while the chip itself is
            // behind "+N more". Once, as soon as the moved-to month renders it.
            if (view === 'month' && !dayFlashed) {
                const cell = scope.querySelector<HTMLElement>(`[data-day="${moment(target).format('YYYY-MM-DD')}"]`);
                if (cell) {
                    dayFlashed = true;
                    flash(cell, styles.cellRevealing);
                }
            }
            const chips = Array.from(scope.querySelectorAll<HTMLElement>('[data-occurrence-key]'));
            const key = pickRevealKey(chips.map(el => el.dataset.occurrenceKey ?? ''), revealRequest);
            const chip = key ? chips.find(el => el.dataset.occurrenceKey === key) : undefined;
            if (chip) {
                chip.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
                flash(chip, styles.chipRevealing);
                return;
            }
            attempts += 1;
            // Not rendered after the move: in a month cell it is behind "+N
            // more", so open that day's panel — its rows are chips too.
            if (attempts === 3 && view === 'month') setPanelDate(moment(target).startOf('day').toDate());
            if (attempts < 15) timer = setTimeout(attempt, 120);
        };
        timer = setTimeout(attempt, 80);
        return () => clearTimeout(timer);
        // Keyed on the nonce: each click is a new request, even for the same row.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revealRequest?.nonce]);

    return (
        <div
            ref={rootRef}
            className={[styles.root, dropRefusal ? styles.rootRefusing : ''].filter(Boolean).join(' ')}
            {...gridHandlers}
        >
            {!isExtensionContext() && <WebsiteNavigateBridge navigateRef={websiteNavigateRef} />}
            <CalendarToolbarV2
                view={view}
                date={date}
                count={visibleCount}
                emptyCopy={emptyCopy}
                busy={busyVisible}
                loadFailed={sentHistory.failed}
                onRetry={sentHistory.retry}
                onShowSent={view === 'month' ? undefined : showSent}
                isRtl={isRtl}
                onView={next => dispatch(setView(next as any))}
                onNavigate={next => dispatch(setCurrentDate(next.toISOString() as any))}
                // BE#657 (a) — the toolbar "New message" button carries no time context (unlike a
                // day-cell "+" or a week/day slot click), so open a TRULY FRESH compose: no now-seed,
                // summary says "pick a date/time", Send gated. Day-cell/slot clicks still seed their
                // chosen time via onCreateAt.
                onCreate={() => dispatch(handleNewMessageDialogThunk({ fresh: true }))}
            />

            <div className={styles.body}>
                <div className={styles.main} aria-busy={rangeLoading || undefined}>
                    {/* An empty MONTH is the one screen state where the eye has
                        nothing to anchor to: the whole viewport is blank cells and
                        the only explanation is an 11.5px line in the far top-left
                        corner of the toolbar, which is not where anyone is looking.
                        This is deliberately NOT a call to action — every cell is
                        already a create target and a button in the middle would
                        collide with that — it is a caption, `pointer-events: none`,
                        that intercepts nothing and simply puts the toolbar's own
                        sentence where the gaze lands. Same source of truth as the
                        toolbar (`emptyPeriodCopy`), so it makes the same distinction
                        between "nothing scheduled", "your filters are hiding
                        everything" and "nothing left — N already went out". Exactly
                        zero only: a sparse month needs nothing, because a single chip
                        is already an anchor. The one control on it is the way to reveal
                        what it says is hidden; only that button takes pointer events. */}
                    {/* Not while the range is still loading: "nothing scheduled" over a month whose
                        data has not arrived yet would be a claim we cannot make. */}
                    {view === 'month' && visibleCount === 0 && !rangeLoading && !emptyNoteDismissed && (
                        <div className={styles.emptyMonthNote}>
                            <p className={styles.emptyMonthNoteInner}>
                                <button
                                    type="button"
                                    className={styles.emptyMonthNoteClose}
                                    onClick={() => setEmptyNoteDismissed(true)}
                                    aria-label={t('calendar.grid.dismissEmptyNote')}
                                >×</button>
                                {/* The sentence repeats the toolbar's, so it is hidden from
                                    assistive tech; the button is not. */}
                                <span aria-hidden="true">{emptyPeriodText(t, emptyCopy)}</span>
                                {emptyCopy.kind === 'nothing' && emptyCopy.actionKey && (
                                    <button type="button" className={styles.emptyMonthNoteAction} onClick={showSent}>
                                        {t(emptyCopy.actionKey)}
                                    </button>
                                )}
                            </p>
                        </div>
                    )}
                    {view === 'month' ? (
                        <MonthGrid
                            layout={monthLayout}
                            weekStartsOn={weekStartsOn}
                            now={now}
                            drag={drag}
                            dropRefused={!!dropRefusal}
                            gridRef={gridRef}
                            beginDrag={beginDrag}
                            onOpenEvent={onOpenEvent}
                            onHoverStart={onHoverStart}
                            onHoverEnd={clearHover}
                            onOpenDay={setPanelDate}
                            onCreateAt={onCreateAt}
                            onDelete={onRequestDelete}
                        />
                    ) : (
                        <TimeGrid
                            days={days}
                            events={events}
                            now={now}
                            drag={drag}
                            dropRefused={!!dropRefusal}
                            gridRef={gridRef}
                            beginDrag={beginDrag}
                            onOpenEvent={onOpenEvent}
                            onHoverStart={onHoverStart}
                            onHoverEnd={clearHover}
                            onCreateAt={onCreateAt}
                            onDelete={onRequestDelete}
                        />
                    )}
                </div>

                {panelDate && (
                    <DayPanel
                        date={panelDate}
                        events={panelEvents}
                        now={now}
                        drag={drag}
                        onClose={() => setPanelDate(null)}
                        onOpenEvent={onOpenEvent}
                        onHoverStart={onHoverStart}
                        onHoverEnd={clearHover}
                        beginDrag={beginDrag}
                        onDelete={onRequestDelete}
                    />
                )}
            </div>

            {hover && !drag?.active && <ChipHoverCard target={hover} now={now} />}

            {drag?.active && <DragPreview drag={drag} view={view} now={now} refusal={dropRefusalText} />}

            {pastDrop && (
                <MessageConfirmationDialog
                    title={t('calendar.grid.pastDropTitle', 'Move this to a time that has already passed?')}
                    description={t('calendar.grid.pastDropBody', 'It will not be sent at that time — it will just sit in the past.')}
                    confirmLabel={t('calendar.grid.pastDropConfirm', 'Move it anyway')}
                    onClose={(value?: string) => {
                        const target = pastDrop;
                        setPastDrop(null);
                        if (value === undefined || !target) return;
                        void moveDueDate(target.event._id, target.dueDate);
                    }}
                />
            )}

            {recurringDrag && (
                <MessageConfirmationDialog
                    title={t('scheduledMessage.confirmation.editRecurring')}
                    options={confirmationDialogOptions.slice(0, 2)}
                    onClose={(value: any) => {
                        if (value) {
                            void dispatch(onRecurringDialogCloseThunk({ value, event: recurringDrag })).then((res) => {
                                // #1257: the original's patch now rejects when it was not saved — say so.
                                if (onRecurringDialogCloseThunk.rejected.match(res)) {
                                    toastDBCreateError(res.error, t('calendar.grid.changeNotSaved', 'Your change was not saved.'));
                                }
                            });
                        }
                        setRecurringDrag(null);
                    }}
                />
            )}

            {deleteTarget && (() => {
                // What is about to be removed, named: the customer is deleting an
                // INSTANT, not a document, and a dialog that will not say which
                // one turns a confident click into a nervous one. Same format the
                // hover card uses, so the sentence reads back the chip they came
                // from.
                const plan = planOccurrenceDelete(deleteTarget);
                if (plan.kind === 'none') return null;
                const when = formatInLanguage(
                    deleteTarget.dueDate as any,
                    i18n.language,
                    t('calendar.grid.hoverDateFormat'),
                );
                // #592b — a future recurring-drip occurrence: not a delete, a
                // per-date SKIP. Distinct (non-destructive) dialog; the series
                // continues, this one date is excluded via an EXDATE server-side.
                if (plan.kind === 'skip-drip-date') {
                    return (
                        <MessageConfirmationDialog
                            title={t('campaignsRedesign.actions.skipThisDateTitle', 'Skip this send?')}
                            description={(
                                <>
                                    <div className={styles.deleteSubject}>
                                        <b className={styles.deleteSubjectWhen}>{when}</b>
                                    </div>
                                    {t('campaignsRedesign.actions.skipThisDatePrompt', 'This date will be skipped. The rest of the sequence keeps sending.')}
                                </>
                            )}
                            confirmLabel={t('campaignsRedesign.actions.skipThisDate', 'Skip this date')}
                            shouldShowCancelButton={true}
                            onClose={(value?: string) => {
                                const target = deleteTarget;
                                setDeleteTarget(null);
                                const outcome = resolveDeleteConfirmation(target, value);
                                if (outcome.kind === 'skip-drip-date') {
                                    void skipDripOccurrence({ dripId: outcome.dripId, dueDate: outcome.occurrenceStartIso });
                                }
                            }}
                        />
                    );
                }
                const scoped = plan.kind === 'scoped';
                // ...and WHICH one. The date alone was not identity: on a day of
                // similar sends — the exact day someone opens this dialog — every
                // candidate carries the same date, so the sentence named the one
                // fact that could not tell them apart. The recipient and the
                // opening words can, and they are already on the chip and the
                // hover card the customer came from.
                //
                // A block, not a longer sentence: three facts interpolated into
                // one string is grammar built in code, it does not survive
                // translation, and it puts an LTR clock inside an RTL run. Each
                // line is its own element, so each takes the direction it should.
                const audience = destinationName(
                    deleteTarget,
                    sendKindOf(deleteTarget),
                    t('scheduledMessage.status.myStatus'),
                );
                const body = (deleteTarget.isPoll ? deleteTarget.poll?.question : deleteTarget.message)
                    || t('btgpt.widgets.noMessageContent');
                const subject = (
                    <>
                        <div className={styles.deleteSubject}>
                            <b className={styles.deleteSubjectWhen}>{when}</b>
                            {audience && (
                                <span className={styles.deleteSubjectTo}>
                                    {t('calendar.grid.to', { names: audience })}
                                </span>
                            )}
                            <span className={styles.deleteSubjectBody}>{body}</span>
                        </div>
                        {scoped
                            ? t('calendar.grid.deleteOccurrencePrompt')
                            : t('calendar.grid.deleteOnePrompt')}
                    </>
                );
                return (
                    <MessageConfirmationDialog
                        // The branch is on the OUTSIDE of t(), not on its key
                        // argument: a key assembled inside the call is invisible
                        // to the translation-key guard, which then has to carry
                        // the call on its unresolved ledger — and that ledger's
                        // numbers are only allowed to go down.
                        title={scoped
                            ? t('scheduledMessage.confirmation.deleteRecurringTitle')
                            : t('scheduledMessage.confirmation.deleteTitle')}
                        // A single send is not offered a choice between three
                        // words for the same act — see planOccurrenceDelete.
                        description={subject}
                        options={scoped ? plan.options : undefined}
                        selected={scoped ? plan.defaultIndex : undefined}
                        destructive={true}
                        shouldShowCancelButton={true}
                        onClose={(value?: string) => {
                            const target = deleteTarget;
                            setDeleteTarget(null);
                            // Every judgement lives in the pure resolver — including
                            // "Cancel, Escape and dismissed all mean nothing" and
                            // "a value that is not an offered scope is not a delete".
                            // NOTHING has been written above this line, on any branch.
                            const outcome = resolveDeleteConfirmation(target, value);
                            if (outcome.kind === 'recurring') {
                                dispatch(onDeleteRecurringDialogCloseThunk(outcome.request));
                            } else if (outcome.kind === 'single') {
                                dispatch(deleteUserMessage(outcome._id));
                            }
                        }}
                    />
                );
            })()}

            {/* #592c — the recurring-drip chip menu: anchored at the pointer.
                "This message" toggles between SKIP (opens the step-skip confirm)
                and UNDO SKIP (non-destructive, fires straight away) depending on
                whether this occurrence's step is already skipped; "whole cycle"
                falls through to the existing per-date skip dialog (deleteTarget).
                Whether the step is skipped is decided by a pure predicate over the
                drip's skip data — the SAME logic WhatsAppSelectors uses to hide a
                skipped occurrence, so the two never disagree. */}
            {skipMenu && (() => {
                const ev = skipMenu.event;
                const plan = planOccurrenceDelete(ev);
                const drip = (ev.dripId ? dripsSelectOneObjectByQuery(dripSlice, { _id: ev.dripId }) : null) as
                    { skippedSteps?: number[]; pendingStepSkips?: PendingStepSkipEntry[]; schedule?: { dueDate?: string | number | Date | null } } | null;
                const stepSkipped = plan.kind === 'skip-drip-date' && isChipStepSkipped({
                    skippedSteps: drip?.skippedSteps,
                    pendingStepSkips: drip?.pendingStepSkips,
                    dripDueDate: drip?.schedule?.dueDate,
                    occurrenceStartMs: ev.dripOccurrenceStartMs,
                    stepIndex: plan.stepIndex,
                });
                return (
                    <Menu
                        open
                        onClose={() => setSkipMenu(null)}
                        anchorReference="anchorPosition"
                        anchorPosition={{ top: skipMenu.y, left: skipMenu.x }}
                    >
                        {/* #592c / BE#648 — single-STEP skip/un-skip is HIDDEN on the calendar
                            (DST occurrence-date divergence); it ships only via the sequence-detail
                            step cards (current-cycle, DST-immune). See CALENDAR_STEP_SKIP_ENABLED. */}
                        {CALENDAR_STEP_SKIP_ENABLED && (stepSkipped ? (
                            <MenuItem onClick={async () => {
                                setSkipMenu(null);
                                const outcome = resolveStepUnskipConfirmation(ev);
                                if (outcome.kind !== 'unskip-drip-step') return;
                                // F2 — await + read status: undoing a skip the drip has already
                                // advanced past answers `too-late` (HTTP 200). The optimistic
                                // chip restore happens in the mutation's onQueryStarted; surface why.
                                try {
                                    const res = await unskipDripStep({
                                        dripId: outcome.dripId,
                                        occurrenceDate: outcome.occurrenceStartIso,
                                        stepIndex: outcome.stepIndex,
                                    }).unwrap();
                                    if (res?.status === 'too-late') {
                                        toast.error(t('campaignsRedesign.actions.unskipTooLate', 'This message has already been sent — the skip can no longer be undone.'));
                                    } else if (res?.status !== 'unskipped') {
                                        toast.error(t('campaignsRedesign.actions.unskipFailed', "Couldn't undo the skip. Please try again."));
                                    }
                                } catch {
                                    toast.error(t('campaignsRedesign.actions.unskipFailed', "Couldn't undo the skip. Please try again."));
                                }
                            }}>
                                {t('campaignsRedesign.actions.undoSkip', 'Undo skip')}
                            </MenuItem>
                        ) : (
                            <MenuItem onClick={() => { setSkipMenu(null); setSkipStepTarget(ev); }}>
                                {t('campaignsRedesign.actions.skipThisMessage', 'Skip this message')}
                            </MenuItem>
                        ))}
                        <MenuItem onClick={() => { setSkipMenu(null); setDeleteTarget(ev); }}>
                            {t('campaignsRedesign.actions.skipWholeCycle', 'Skip whole cycle')}
                        </MenuItem>
                    </Menu>
                );
            })()}

            {/* #592c — the single-message skip confirm. Non-destructive: the step
                is skipped for everyone on THIS send; the rest of the sequence and
                the series continue. All judgement is in the pure resolver. */}
            {skipStepTarget && (() => {
                const when = formatInLanguage(
                    skipStepTarget.dueDate,
                    i18n.language,
                    t('calendar.grid.hoverDateFormat'),
                );
                return (
                    <MessageConfirmationDialog
                        title={t('campaignsRedesign.actions.skipThisMessageTitle', 'Skip this message?')}
                        description={(
                            <>
                                <div className={styles.deleteSubject}>
                                    <b className={styles.deleteSubjectWhen}>{when}</b>
                                </div>
                                {t('campaignsRedesign.actions.skipThisMessagePrompt', 'This message will be skipped for everyone on this send. The rest of the sequence keeps sending.')}
                            </>
                        )}
                        confirmLabel={t('campaignsRedesign.actions.skipThisMessageConfirm', 'Skip this message')}
                        shouldShowCancelButton={true}
                        onClose={async (value?: string) => {
                            const target = skipStepTarget;
                            setSkipStepTarget(null);
                            const outcome = resolveStepSkipConfirmation(target, value);
                            if (outcome.kind !== 'skip-drip-step') return;
                            // F2 — await + read status; the optimistic chip flip / revert is
                            // handled centrally in the mutation's onQueryStarted, so a non-success
                            // status (HTTP 200) only needs surfacing to the user here.
                            try {
                                const res = await skipDripStep({
                                    dripId: outcome.dripId,
                                    occurrenceDate: outcome.occurrenceStartIso,
                                    stepIndex: outcome.stepIndex,
                                }).unwrap();
                                if (res?.status !== 'skipped') {
                                    toast.error(t('campaignsRedesign.actions.skipFailed', "Couldn't skip this message. Please try again."));
                                }
                            } catch {
                                toast.error(t('campaignsRedesign.actions.skipFailed', "Couldn't skip this message. Please try again."));
                            }
                        }}
                    />
                );
            })()}

            {warning && (
                // A refusal is INFORMATION, not a confirmation. Passing only a
                // title let the dialog fall back to its delete copy — the body
                // read "Are you sure you want to remove this message?" and the
                // button said Delete, under a sentence explaining why a drop
                // could not happen. Say what happened, and offer one way out.
                <MessageConfirmationDialog
                    title={t('calendar.grid.dropNotPossible', "That move isn't possible")}
                    description={warning}
                    confirmLabel={t('common.gotIt', 'Got it')}
                    onClose={() => setWarning(null)}
                    shouldShowCancelButton={false}
                />
            )}
        </div>
    );
};

export default CalendarGridV2;
